import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import useSWR from 'swr';
import { AuthContext } from '@dashboard/context/AuthContext';
import request from '@dashboard/services/request';
import Button from '@dashboard/components/Button';
import { Formik } from 'formik';
import { TextInput, Select } from '@dashboard/components/Formik';
import { FormElement, Label } from '@dashboard/components/Form';
import { countries, isEU } from '@dashboard/services/countries/countries';
import FormSubmit from '@dashboard/components/Formik/components/FormSubmit';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { isEmpty } from 'lodash';

const { REACT_APP_API_URL: apiUrl } = process.env;

const BillingAddress = ({ initialIsEditing = false, onCancelEditing, onSubmit }) => {
  const [isEditing, setIsEditing] = useState(initialIsEditing);
  const { sellerId, token } = useContext(AuthContext);

  const { data, mutate } = useSWR(apiUrl + `/sellers/${sellerId}/billing-address`, url =>
    request(url, token),
  );

  if (isEditing) {
    return (
      <BillingAddressForm
        address={data?.data}
        setIsEditing={setIsEditing}
        onCancelEditing={onCancelEditing}
        updateAddress={data => {
          mutate(data);
          if (onSubmit && typeof onSubmit === 'function') {
            onSubmit(data);
          }
        }}
      />
    );
  }

  return <BillingAddressDisplay address={data?.data} setIsEditing={setIsEditing} />;
};

BillingAddress.propTypes = {
  initialIsEditing: PropTypes.bool,
  onCancelEditing: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default BillingAddress;

const BillingAddressDisplay = ({ address, setIsEditing }) => (
  <>
    {address && (
      <div>
        {address.firstName} {address.lastName}
        <br />
        {address.companyName && (
          <>
            {address.companyName}
            <br />
          </>
        )}
        {address.streetAddress}
        <br />
        {address.streetAddress2 && (
          <>
            {address.streetAddress2}
            <br />
          </>
        )}
        {address.city} {address.state}
        <br />
        {address.postalCode}
        <br />
        {countries.find(country => country.alpha2 === address.country)?.name}
        {address.vatNumber && (
          <>
            <br />
            <br />
            VAT Number: {address.vatNumber}
          </>
        )}
      </div>
    )}

    {!address && <div className="text-sm">You haven't added a business address yet</div>}

    <FormSubmit>
      <Button appearance="light" onClick={() => setIsEditing(true)}>
        {address ? 'Edit address' : 'Add address'}
      </Button>
    </FormSubmit>
  </>
);

const BillingAddressForm = ({ address = {}, setIsEditing, updateAddress, onCancelEditing }) => {
  const { sellerId, token } = useContext(AuthContext);

  return (
    <Formik
      initialValues={{
        firstName: address.firstName,
        lastName: address.lastName,
        companyName: address.companyName,
        streetAddress: address.streetAddress,
        streetAddress2: address.streetAddress2,
        city: address.city,
        state: address.state,
        postalCode: address.postalCode,
        country: address.country,
        vatNumber: address.vatNumber,
      }}
      onSubmit={async (values, actions) => {
        actions.setStatus('');

        try {
          const result = await request(apiUrl + `/sellers/${sellerId}/billing-address`, token, {
            body: JSON.stringify(values),
            method: 'POST',
          });

          if (!result.data) {
            throw result;
          }

          actions.setSubmitting(false);
          updateAddress(result.data);
          setIsEditing(false);
        } catch (err) {
          const { status, errors } = transformApiErrorToFormik(err);

          if (!isEmpty(errors)) {
            actions.setErrors(errors);
          } else {
            actions.setStatus(status);
          }

          actions.setSubmitting(false);
        }
      }}
    >
      {({ values, status, handleSubmit }) => (
        <form onSubmit={handleSubmit} autoComplete>
          <div style={{ display: 'flex' }}>
            <FormElement style={{ marginRight: '1rem', flex: '1' }}>
              <Label htmlFor="firstName">First name</Label>
              <TextInput
                name="firstName"
                id="firstName"
                type="text"
                placeholder="First name"
                required
                border
              />
              <FormError name="firstName" />
            </FormElement>

            <FormElement style={{ flex: '1' }}>
              <Label htmlFor="lastName">Last name</Label>
              <TextInput
                name="lastName"
                id="lastName"
                type="text"
                placeholder="Last name"
                required
                border
              />
              <FormError name="lastName" />
            </FormElement>
          </div>

          <FormElement>
            <Label htmlFor="companyName" optional>
              Company name
            </Label>
            <TextInput
              name="companyName"
              id="companyName"
              type="text"
              placeholder="Company name"
              border
            />
            <FormError name="companyName" />
          </FormElement>

          <FormElement>
            <Label htmlFor="streetAddress">Address line 1</Label>
            <TextInput
              name="streetAddress"
              id="streetAddress"
              type="text"
              placeholder="Address line 1"
              required
              border
            />
            <FormError name="streetAddress" />
          </FormElement>

          <FormElement>
            <Label htmlFor="streetAddress2" optional>
              Address line 2
            </Label>
            <TextInput
              name="streetAddress2"
              id="streetAddress2"
              type="text"
              placeholder="Address line 2"
              border
            />
            <FormError name="streetAddress2" />
          </FormElement>

          <div style={{ display: 'flex' }}>
            <FormElement style={{ marginRight: '1rem', flex: '1' }}>
              <Label htmlFor="country">Country</Label>

              <Select name="country" id="country" required border placeholder="Country">
                <option>Select country</option>

                {countries.map(country => (
                  <option value={country.alpha2} key={country.alpha2}>
                    {country.name}
                  </option>
                ))}
              </Select>
              <FormError name="country" />
            </FormElement>

            <FormElement style={{ flex: '1' }}>
              <Label htmlFor="city">City</Label>
              <TextInput name="city" id="city" type="text" placeholder="City" required border />
              <FormError name="city" />
            </FormElement>
          </div>

          <div style={{ display: 'flex' }}>
            <FormElement style={{ marginRight: '1rem', flex: '1' }}>
              <Label htmlFor="state" optional>
                State
              </Label>
              <TextInput name="state" id="state" type="text" placeholder="State" border />
            </FormElement>

            <FormElement style={{ flex: '1' }}>
              <Label htmlFor="postalCode">Postal code / Zip</Label>
              <TextInput
                name="postalCode"
                id="postalCode"
                type="text"
                placeholder="Postal code / Zip"
                required
                border
              />
              <FormError name="postalCode" />
            </FormElement>
          </div>

          {values.country && isEU(values.country) && (
            <FormElement>
              <Label htmlFor="vatNumber">VAT Number</Label>
              <TextInput
                name="vatNumber"
                id="vatNumber"
                type="text"
                placeholder="VAT Number"
                border
              />
              <FormError name="vatNumber" />
            </FormElement>
          )}

          <FormErrorBase>{status && status}</FormErrorBase>

          <FormSubmit>
            <FormSubmit.Button>Save</FormSubmit.Button>

            <Button
              className="ml-2"
              type="button"
              appearance="light"
              onClick={() => {
                setIsEditing(false);

                if (onCancelEditing && typeof onCancelEditing === 'function') {
                  onCancelEditing();
                }
              }}
            >
              Cancel
            </Button>
          </FormSubmit>
        </form>
      )}
    </Formik>
  );
};
